import { borderRadius, palette, spaces } from '@kimuno/theme';
import { Box, OutlinedInput, Stack, Typography } from '@mui/material';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getTemporaryAttachments } from '../../../../../reducers/aiAssistant.selectors';
import { useId } from '../../../../../utils/useId';
import useScreenSize from '../../../../../utils/useScreenSize';
import AIUploadFileList from './AIUploadFileList';
import DictationWaveform from './DictationWaveform';

type PromptInputProps = {
	onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
	promptValue: string;
	maxPromptLength?: number;
	placeholder?: string;
	leftItems?: React.ReactNode;
	rightItems?: React.ReactNode;
	'aria-label'?: string;
	dictationActive?: boolean;
	inputRef?: React.Ref<HTMLTextAreaElement>;
};

const PromptInput: React.FC<PromptInputProps> = ({
	onChange,
	onKeyDown,
	promptValue,
	maxPromptLength,
	placeholder,
	leftItems,
	rightItems,
	'aria-label': ariaLabel,
	dictationActive = false,
	inputRef,
}) => {
	const { t } = useTranslation();
	const { isXsScreen, isSmScreen } = useScreenSize();
	const temporaryAttachments = useSelector(getTemporaryAttachments);
	const fieldId = useId('ai-prompt-input');
	const characterCountId = `${fieldId}-character-count`;

	const getMaxInputRows = () => {
		if (isXsScreen) {
			return 5;
		}
		if (isSmScreen) {
			return 10;
		}
		return 20;
	};

	const isAtMaxLength = maxPromptLength ? promptValue.length >= maxPromptLength : false;
	// An empty input has nothing to show while dictating: the waveform takes its place.
	const isInputHidden = dictationActive && promptValue === '';

	return (
		<Box sx={{ position: 'relative' }}>
			<Stack
				gap={spaces.xs}
				sx={{
					padding: spaces.xs,
					borderRadius: borderRadius.md,
					backgroundColor: palette.background.paperElevation2,
				}}
			>
				{temporaryAttachments.length > 0 && (
					<AIUploadFileList uploadedAttachments={temporaryAttachments} withRemoveButton />
				)}
				{!isInputHidden && (
					<OutlinedInput
						id={fieldId}
						data-testid="ai-assistant-prompt-input"
						onChange={onChange}
						onKeyDown={onKeyDown}
						value={promptValue}
						inputRef={inputRef}
						readOnly={dictationActive}
						minRows={1}
						maxRows={getMaxInputRows()}
						multiline
						placeholder={dictationActive ? undefined : placeholder}
						fullWidth
						aria-describedby={maxPromptLength ? characterCountId : undefined}
						aria-invalid={isAtMaxLength ? true : undefined}
						sx={{
							padding: 0,
							'& .MuiOutlinedInput-notchedOutline': {
								border: 'none',
							},
						}}
						inputProps={{
							// On the textarea itself: its placeholder, the fallback name, goes while dictating.
							'aria-label': ariaLabel || placeholder || t('AI Assistant prompt input'),
							sx: {
								padding: `${spaces.xs} ${spaces.sm}`,
								borderRadius: borderRadius.sm,
							},
						}}
					/>
				)}
				{dictationActive && <DictationWaveform />}
				<Stack direction="row" justifyContent="space-between">
					<Stack direction="row" gap={spaces.xs} alignItems="center">
						{leftItems}
					</Stack>
					<Stack direction="row" gap={spaces.xs} alignItems="center">
						{rightItems}
					</Stack>
				</Stack>
			</Stack>
			{maxPromptLength && (
				<Box sx={{ paddingLeft: spaces.md, paddingTop: spaces.xxs }}>
					<Typography
						id={characterCountId}
						data-testid="ai-assistant-user-prompt-length"
						variant="caption"
						color={isAtMaxLength ? palette.error.main : palette.text.secondary}
						role="status"
						aria-live="polite"
						aria-atomic="true"
					>
						{promptValue.length}/{maxPromptLength} {t('characters')}
					</Typography>
				</Box>
			)}
		</Box>
	);
};

export default PromptInput;
